import React, { FC, useEffect, useContext } from 'react';
import { connect } from 'react-redux';
import { makeStyles } from '@material-ui/core';
import Grid from '@material-ui/core/Grid';
import { MtsCard, MtsCardBody, MtsLink } from '@mts-ds/components-react';

import { Dispatch, iRootState } from '../../store';
import { IAlert, INotificationClass, NotificationSeverity } from '../../models/types';
import { shouldPlaySound } from './utils';

import NotificationIcon from '../../components_/Notification/NotificationIcon';

import Typography from '../../components_/Typography';
import moment from 'moment';
import { Space, Dropdown, Button } from 'antd';
import type { MenuProps } from 'antd';
import { EllipsisOutlined } from '@ant-design/icons';
import { ToastContext } from '../../components_/Toast';
import { useAlertNavigation } from './useAlertNavigation';

const useStyles = makeStyles(({ getColor, spacing }) => ({
  body: {
    color: getColor('text-primary'),
    padding: spacing(2),
    position: 'relative'
  },
  info: {
    '& > *:not(:last-child)': {
      marginBottom: spacing(0.5)
    },
    hyphens: 'auto'
  },
  close: {
    position: 'absolute',
    right: 4,
    top: 4
  }
}));

const mapState = (state: iRootState) => ({
  alerts: state.alerts.list,
  admin: state.admin
});

const mapDispatch = (dispatch: any) => {
  const d = dispatch as Dispatch;

  return {
    confirmAlert: d.alerts.confirmAlert,
    deleteAlert: d.alerts.deleteAlert,
    addConfirmation: d.alerts.addConfirmation,
    postponeAlert: d.alerts.postponeAlert
  };
};

type connectedProps = ReturnType<typeof mapState> & ReturnType<typeof mapDispatch>;

type IProps = {
  alert: IAlert;
  play: any;
  stop: any;
} & connectedProps;

const Item: FC<IProps> = ({
  alert,
  deleteAlert,
  addConfirmation,
  postponeAlert,
  confirmAlert,
  play,
  stop,
  admin
}) => {
  const { uid, type, title, description, isConfirm, severity, date } = alert;
  const classes = useStyles();
  const toast = useContext(ToastContext);
  const focusAlertTarget = useAlertNavigation();
  const showError = (error: any, fallback: string) => {
    const typeStr = String(error?.response?.data?.type ?? '');
    const friendlyError =
      (typeStr.toLowerCase().includes('friendly') && ` ${error?.response?.data?.detail ?? ''}`) ||
      '';
    toast({
      text: friendlyError ? fallback + ': ' + friendlyError : fallback + '.',
      type: 'error'
    });
  };

  // Меню "Отложить" — секунды из /config/company.hazardTimer.delayTimerSeconds
  const delaySeconds: number[] = ((admin as any)?.hazardTimer?.delayTimerSeconds || []) as number[];
  const formatDelay = (s: number) => {
    const ld = moment.localeData();
    if (s % 60 === 0) {
      const m = s / 60;
      return ld.relativeTime(m, true, 'mm', false);
    }
    return ld.relativeTime(s, true, 'ss', false);
  };
  const postponeItems: MenuProps['items'] = delaySeconds.map((s: number) => ({
    key: String(s),
    label: formatDelay(s)
  }));

  const onPostponeMenuClick: MenuProps['onClick'] = async ({ key }) => {
    const error = await postponeAlert({ id: uid, seconds: Number(key) });
    if (error) {
      showError(error, 'Ошибка при откладывании уведомления');
      return;
    }
    stop();
    deleteAlert(uid);
  };

  const handlePostponeDefault = () => {
    if (delaySeconds.length) {
      const def = delaySeconds[0];
      // эмулируем выбор первого пункта
      onPostponeMenuClick({ key: String(def) } as any);
    }
  };

  // Меню "Подтвердить" — варианты text[] из /config/company.notificationConfirmText, используем единый type READ
  const confirmList = ((admin as any)?.notificationConfirmText || []) as string[];
  const confirmItems: MenuProps['items'] = confirmList.map((text, idx) => ({
    key: String(idx),
    label: String(text)
  }));

  const onConfirmMenuClick: MenuProps['onClick'] = async ({ key }) => {
    const idx = Number(key);
    const text = confirmList[idx] ?? '';
    const error = await confirmAlert({
      uid,
      type: 'READ',
      text,
      isSuccessfull: true,
      time: new Date().toISOString(),
      userId: (alert as any).userId,
      userDevice: 'web',
      senderAddress: window.location.origin,
      senderMessageId: '',
      senderMessage: '',
      retryCount: 0
    });
    if (error) {
      showError(error, 'Ошибка подтверждения уведомления');
      return;
    }
    stop();
    deleteAlert(uid);
  };

  const handleConfirmDefault = async () => {
    const error = await confirmAlert({
      uid,
      type: 'READ',
      text: '',
      isSuccessfull: true,
      time: new Date().toISOString(),
      userId: (alert as any).userId,
      userDevice: 'web',
      senderAddress: window.location.origin,
      senderMessageId: '',
      senderMessage: '',
      retryCount: 0
    });
    if (error) {
      showError(error, 'Ошибка подтверждения уведомления');
      return;
    }
    stop();
    deleteAlert(uid);
  };


  const soundStart = async () => {
    if (shouldPlaySound(severity, alert.needSound)) {
      play(uid);
    }
  };

  // Ссылка видна, если уведомление указывает место (план или офис) — координаты не обязательны:
  // без них откроется план целиком
  const canNavigate = Boolean(alert?.officeId || alert?.placeId || alert?.mapId);

  const handleNavigate = () => focusAlertTarget(alert, { manual: true });

  useEffect(() => {
    soundStart();

    switch (severity) {
      case NotificationSeverity.Med:
        deleteAlert(uid);
        break;
      case NotificationSeverity.Low:
        deleteAlert(uid);
        break;
      case NotificationSeverity.High:
        deleteAlert(uid);
        break;
      case NotificationSeverity.Ignored:
        deleteAlert(uid);
        break;
    }

    const handler = setTimeout(() => {
      switch (severity) {
        case NotificationSeverity.Critical:
          addConfirmation(alert);
          break;
        default:
          deleteAlert(uid);
          break;
      }
    }, 10000);

    return () => {
      clearTimeout(handler);
    };
  }, []);

  return (
    <MtsCard>
      <MtsCardBody className={classes.body}>
        <Grid container spacing={3}>
          <Grid item xs={2}>
            <NotificationIcon notificationClass={type as INotificationClass} />
          </Grid>
          <Grid item xs={8} className={classes.info}>
            <Typography variant="p2Medium">{title}</Typography>
            <Typography variant="p2Regular">
              <div
                dangerouslySetInnerHTML={{
                  __html: moment(date).format('DD.MM.YYYY HH:mm:ss') + ' - ' + description
                }}
              />
            </Typography>
            {canNavigate && (
              <Typography variant="p2Regular">
                <MtsLink onClick={handleNavigate}>Перейти к месту</MtsLink>
              </Typography>
            )}
            <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
              {Boolean(delaySeconds.length) && (
                <Space.Compact>
                  <Button color="default" variant="filled" size="middle" onClick={handlePostponeDefault}>Отложить</Button>
                  <Dropdown
                    menu={{ items: postponeItems, onClick: onPostponeMenuClick }}
                    placement="bottomRight"
                    trigger={['click']}
                    getPopupContainer={() => document.body}
                  >
                    <Button color="default" variant="filled" size="middle" icon={<EllipsisOutlined />} />
                  </Dropdown>
                </Space.Compact>
              )}
              <Space.Compact>
                <Button type='primary' size="middle" onClick={handleConfirmDefault}>
                  Подтвердить
                </Button>
                {Boolean(confirmItems.length) && (
                  <Dropdown
                    menu={{ items: confirmItems, onClick: onConfirmMenuClick }}
                    placement="bottomRight"
                    trigger={['click']}
                    getPopupContainer={() => document.body}
                  >
                    <Button type='primary' size="middle" icon={<EllipsisOutlined />} />
                  </Dropdown>
                )}
              </Space.Compact>
            </div>
          </Grid>
          {/* {dateMap.map((stamp, index) => (
              <Grid key={index} container justify="flex-end">
                <Typography variant="p3Regular">{stamp}</Typography>
              </Grid>
            ))} */}
        </Grid>
      </MtsCardBody>
    </MtsCard>
  );
};

const withConnect = connect(mapState, mapDispatch);

export default withConnect(Item);
